/**
 * Always-on "Live Class" card for the student & teacher dashboards.
 * - Student: sees a Join button the moment a coach is live; otherwise a friendly idle state + next scheduled class.
 * - Teacher: can Go Live instantly or Schedule a class, join/resume/end an active class, and manage upcoming ones.
 * Powered by the embedded Jitsi room (JitsiRoom).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Radio, Video, CalendarClock, PlayCircle, X, Plus, Trash2, Loader2, CircleDot } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import JitsiRoom from './JitsiRoom';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const GROUP_OPTS = [
  ['all', 'All Students'], ['g4', 'Grade 4'], ['g5', 'Grade 5'],
  ['g67', 'Grades 6-7'], ['g8', 'Grade 8'], ['g9', 'Grade 9'],
];

const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

export default function LiveClassCard({ isTeacher = false }) {
  const [session, setSession] = useState(null);
  const [domain, setDomain] = useState('meet.jit.si');
  const [upcoming, setUpcoming] = useState([]);
  const [joined, setJoined] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', when: '', scope: 'all', grade_group: 'g5' });
  const token = localStorage.getItem('academy_token');
  // STABLE onClose reference so JitsiRoom's useEffect isn't torn down every
  // time this card polls or re-renders. Previously this was `() => setJoined(false)`
  // inline in JSX → a new function ref every render → JitsiRoom effect fired
  // → iframe rebooted every ~5s once Jitsi hit `videoConferenceJoined`.
  const handleLeaveRoom = useCallback(() => setJoined(false), []);

  const poll = useCallback(async () => {
    if (!token) return;
    try {
      const [a, u] = await Promise.all([
        api.get('/academy/live/active', { headers: auth() }),
        api.get('/academy/live/upcoming', { headers: auth() }),
      ]);
      setSession(a.data.session || null);
      if (a.data.domain) setDomain(a.data.domain);
      if (!a.data.session) setJoined(false);
      setUpcoming(u.data.upcoming || []);
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => { poll(); const t = setInterval(poll, 20000); return () => clearInterval(t); }, [poll]);

  const goLiveNow = async () => {
    setBusy(true);
    try {
      const r = await api.post('/academy/live/quickstart', { title: '' }, { headers: auth() });
      if (r.data.domain) setDomain(r.data.domain);
      toast.success('You are live!');
      await poll();
      setJoined(true);
    } catch { toast.error('Could not start the class'); } finally { setBusy(false); }
  };

  const endClass = async () => {
    if (!session) return;
    setBusy(true);
    try { await api.post(`/academy/live/${session.id}/end`, {}, { headers: auth() }); setJoined(false); toast.success('Class ended'); await poll(); }
    catch { toast.error('Could not end the class'); } finally { setBusy(false); }
  };

  const schedule = async () => {
    if (!form.title.trim() || !form.when) { toast.error('Add a title and a date/time'); return; }
    setBusy(true);
    try {
      await api.post('/academy/live/schedule', {
        title: form.title.trim(),
        scheduled_at: new Date(form.when).toISOString(),
        scope: form.scope,
        grade_group: form.scope === 'grade' ? form.grade_group : null,
      }, { headers: auth() });
      toast.success('Class scheduled');
      setShowForm(false); setForm({ title: '', when: '', scope: 'all', grade_group: 'g5' });
      await poll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not schedule'); } finally { setBusy(false); }
  };

  const goLiveScheduled = async (id) => {
    setBusy(true);
    try { const r = await api.post(`/academy/live/${id}/golive`, {}, { headers: auth() }); if (r.data.domain) setDomain(r.data.domain); toast.success('You are live!'); await poll(); setJoined(true); }
    catch { toast.error('Could not go live'); } finally { setBusy(false); }
  };

  const cancelScheduled = async (id) => {
    setBusy(true);
    try { await api.delete(`/academy/live/${id}`, { headers: auth() }); toast.success('Removed'); await poll(); }
    catch { toast.error('Could not remove'); } finally { setBusy(false); }
  };

  const accent = isTeacher ? 'emerald' : 'red';
  const isLive = !!session;

  return (
    <div className="mb-6" data-testid="live-class-card">
      <div className={`border-2 ${isLive ? 'border-red-500/60 bg-gradient-to-r from-red-500/10 to-transparent' : 'border-zinc-800 bg-black/40'} rounded-xl p-4`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {isLive
              ? <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-red-400"><Radio className="w-3.5 h-3.5 animate-pulse" /> Live Now</span>
              : <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-500"><CircleDot className="w-3.5 h-3.5" /> Live Class</span>}
            <div className="min-w-0">
              {isLive ? (
                <>
                  <div className="text-sm font-black text-white truncate">{session.title}</div>
                  <div className="text-[11px] text-zinc-400">Hosted by {session.host_name} · {session.scope_label}</div>
                </>
              ) : (
                <>
                  <div className="text-sm font-black text-white">{isTeacher ? 'Start or schedule a live class' : 'No live class in session right now'}</div>
                  <div className="text-[11px] text-zinc-500">{isTeacher ? 'Students see a Join button the moment you go live.' : "You'll see a Join button here the moment your coach goes live."}</div>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {isLive && (
              <button onClick={() => setJoined(true)} className="flex items-center gap-2 bg-red-500 hover:bg-red-400 text-white px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors rounded" data-testid="join-live-class-btn">
                <Video className="w-4 h-4" /> {isTeacher ? 'Resume Class' : 'Join Live Class'}
              </button>
            )}
            {isTeacher && isLive && (
              <button onClick={endClass} disabled={busy} className="flex items-center gap-2 border-2 border-zinc-700 hover:border-red-500 text-zinc-300 px-3 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors rounded" data-testid="end-live-class-btn">
                <X className="w-4 h-4" /> End
              </button>
            )}
            {isTeacher && !isLive && (
              <>
                <button onClick={goLiveNow} disabled={busy} className={`flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors rounded disabled:opacity-50`} data-testid="go-live-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Go Live Now
                </button>
                <button onClick={() => setShowForm((s) => !s)} className="flex items-center gap-2 border-2 border-emerald-500/50 text-emerald-300 px-3 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors rounded" data-testid="schedule-class-btn">
                  <CalendarClock className="w-4 h-4" /> Schedule
                </button>
              </>
            )}
          </div>
        </div>

        {/* Schedule form (teacher) */}
        {isTeacher && showForm && !isLive && (
          <div className="mt-4 grid gap-2 sm:grid-cols-2 border-t border-zinc-800 pt-4" data-testid="schedule-form">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Class title (e.g. Friday Study Hall)" className="bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500 rounded" data-testid="schedule-title" />
            <input type="datetime-local" value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} className="bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500 rounded" data-testid="schedule-when" />
            <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500 rounded" data-testid="schedule-scope">
              <option value="all">All Students</option>
              <option value="grade">Specific Grade</option>
            </select>
            {form.scope === 'grade'
              ? (
                <select value={form.grade_group} onChange={(e) => setForm({ ...form, grade_group: e.target.value })} className="bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500 rounded" data-testid="schedule-grade">
                  {GROUP_OPTS.filter(([k]) => k !== 'all').map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              )
              : <div />}
            <button onClick={schedule} disabled={busy} className="sm:col-span-2 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black py-2.5 text-[10px] font-black uppercase tracking-[0.2em] rounded disabled:opacity-50" data-testid="schedule-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Schedule Class
            </button>
          </div>
        )}

        {/* Upcoming list */}
        {upcoming.length > 0 && (
          <div className="mt-4 border-t border-zinc-800 pt-3 space-y-2" data-testid="upcoming-list">
            <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 font-black">Upcoming</div>
            {upcoming.map((u) => (
              <div key={u.id} className="flex items-center gap-3 bg-black/40 rounded-lg px-3 py-2 border border-zinc-800">
                <CalendarClock className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{u.title}</div>
                  <div className="text-[11px] text-zinc-500">{fmtWhen(u.scheduled_at)} · {u.scope_label}</div>
                </div>
                {isTeacher && (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => goLiveScheduled(u.id)} disabled={busy} className="flex items-center gap-1 bg-emerald-500 hover:bg-emerald-400 text-black px-2.5 py-1.5 text-[9px] font-black uppercase tracking-wider rounded" data-testid={`golive-${u.id}`}><PlayCircle className="w-3.5 h-3.5" /> Go Live</button>
                    <button onClick={() => cancelScheduled(u.id)} disabled={busy} className="text-zinc-500 hover:text-red-400" data-testid={`cancel-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {joined && session && (
        <JitsiRoom room={session.room} displayName={session.display_name} title={session.title} domain={domain} onClose={handleLeaveRoom} />
      )}
    </div>
  );
}
